import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import MemberReportFilterBar from "../components/memberreport/MemberReportFilterBar";
import MemberReportTable from "../components/memberreport/MemberReportTable";
import MemberEditDialog from "../components/memberreport/MemberEditDialog";
import MemberNoteDialog from "../components/memberreport/MemberNoteDialog";
import Pagination from "../components/shared/Pagination";

const ITEMS_PER_PAGE = 20;

const fmtDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildFilterParams = ({ marketingFilter, websiteFilter, followUpFilter, startDate, endDate }) => {
  const params = new URLSearchParams();
  if (marketingFilter) params.append("marketing_name", marketingFilter);
  if (websiteFilter) params.append("website", websiteFilter);
  if (followUpFilter) params.append("closed_by", followUpFilter);
  if (startDate) params.append("start_date", new Date(startDate + "T00:00:00").toISOString());
  if (endDate) params.append("end_date", new Date(endDate + "T23:59:59").toISOString());
  return params;
};

const MemberReportPanel = ({ user }) => {
  const navigate = useNavigate();
  const todayStr = new Date().toLocaleDateString("en-CA");

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [marketingFilter, setMarketingFilter] = useState("");
  const [websiteFilter, setWebsiteFilter] = useState("");
  const [followUpFilter, setFollowUpFilter] = useState("");
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  // Lists for filters
  const [marketingList, setMarketingList] = useState([]);
  const [websiteList, setWebsiteList] = useState([]);
  const [followUpList, setFollowUpList] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Edit
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "" });

  // Note
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteMember, setNoteMember] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [memberNotes, setMemberNotes] = useState([]);
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    fetchMembers();
    fetchFilterLists();
  }, []);

  const fetchMembers = async (overrideDates = null) => {
    setLoading(true);
    try {
      const params = buildFilterParams({
        marketingFilter,
        websiteFilter,
        followUpFilter,
        startDate: overrideDates?.startDate ?? startDate,
        endDate: overrideDates?.endDate ?? endDate,
      });
      const response = await axiosInstance.get("/members/submitted?" + params.toString());
      setMembers(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error("Sesi Anda telah berakhir. Silakan login kembali.");
      } else {
        toast.error(error.response?.data?.detail || "Gagal memuat data member");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterLists = async () => {
    try {
      const [marketing, website, allMembers] = await Promise.all([
        axiosInstance.get("/marketing-list"),
        axiosInstance.get("/website-list"),
        axiosInstance.get("/members/submitted"),
      ]);
      setMarketingList(marketing.data);
      setWebsiteList(website.data);
      const followUps = [...new Set(allMembers.data.map((m) => m.closed_by).filter(Boolean))];
      setFollowUpList(followUps.map((name) => ({ name })));
    } catch (error) {
      console.error("Failed to fetch filter lists");
    }
  };

  const handleApplyFilters = () => {
    fetchMembers();
  };

  const handlePresetDate = async (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case "today":
        start = end = new Date();
        break;
      case "yesterday": {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday;
        break;
      }
      case "this_week": {
        const now = new Date();
        const currentDay = now.getDay();
        const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
        start = new Date(now);
        start.setDate(now.getDate() + mondayOffset);
        end = new Date();
        break;
      }
      case "last_week": {
        const thisWeekStart = new Date();
        const dow = thisWeekStart.getDay();
        const daysToMonday = dow === 0 ? -6 : 1 - dow;
        thisWeekStart.setDate(thisWeekStart.getDate() + daysToMonday);
        end = new Date(thisWeekStart);
        end.setDate(thisWeekStart.getDate() - 1);
        start = new Date(end);
        start.setDate(end.getDate() - 6);
        break;
      }
      case "this_month":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date();
        break;
      case "last_month":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }

    const startDateStr = fmtDate(start);
    const endDateStr = fmtDate(end);
    setStartDate(startDateStr);
    setEndDate(endDateStr);
    // Fetch immediately using the freshly-computed dates (don't rely on state being committed).
    fetchMembers({ startDate: startDateStr, endDate: endDateStr });
  };

  const handleResetFilters = () => {
    setMarketingFilter("");
    setWebsiteFilter("");
    setFollowUpFilter("");
    setStartDate("");
    setEndDate("");
    setTimeout(() => fetchMembers({ startDate: "", endDate: "" }), 100);
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setEditForm({ name: member.marketing_name, phone: member.phone });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.phone) {
      toast.error("Nama dan nomor HP harus diisi");
      return;
    }
    try {
      await axiosInstance.put(`/members/${editingMember.id}`, {
        marketing_name: editForm.name,
        phone: editForm.phone,
      });
      toast.success("Data member berhasil diupdate");
      setShowEditDialog(false);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal update member");
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = buildFilterParams({
        marketingFilter,
        websiteFilter,
        followUpFilter,
        startDate,
        endDate,
      });
      const response = await axiosInstance.get("/export/member-report?" + params.toString(), {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `member_report_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export berhasil!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal export data");
    }
  };

  const handleDeleteMember = async (memberId, memberUsername) => {
    if (!window.confirm(`Yakin ingin menghapus member "${memberUsername}" dari member report? Data yang dihapus tidak dapat dikembalikan.`)) {
      return;
    }
    try {
      await axiosInstance.delete(`/members/${memberId}`);
      toast.success("Member berhasil dihapus");
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menghapus member");
    }
  };

  const openNoteDialog = async (member) => {
    setNoteMember(member);
    setNoteText("");
    setMemberNotes([]);
    setShowNoteDialog(true);
    try {
      const params = new URLSearchParams({
        member_id: member.id,
        action: "note_added",
        page: "1",
        page_size: "5",
      });
      const resp = await axiosInstance.get(`/member-history?${params.toString()}`);
      setMemberNotes(resp.data.logs || []);
    } catch (e) {
      console.error("Failed to fetch existing notes", e);
    }
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) {
      toast.error("Catatan tidak boleh kosong");
      return;
    }
    setSavingNote(true);
    try {
      await axiosInstance.post("/member-history/note", {
        member_id: noteMember.id,
        note: noteText.trim(),
      });
      toast.success(`Catatan untuk ${noteMember.username} berhasil disimpan`);
      setNoteText("");
      const params = new URLSearchParams({
        member_id: noteMember.id,
        action: "note_added",
        page: "1",
        page_size: "5",
      });
      const resp = await axiosInstance.get(`/member-history?${params.toString()}`);
      setMemberNotes(resp.data.logs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan catatan");
    } finally {
      setSavingNote(false);
    }
  };

  const totalPages = Math.ceil(members.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedMembers = members.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const canExport =
    user?.role === "superadmin" || (user?.role === "supervisor" && user?.can_export !== false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]" data-testid="member-report-panel">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/dashboard")}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white hover:bg-red-900/20"
              >
                <ArrowLeft className="mr-2" size={18} />
                Kembali
              </Button>
              <h1 className="text-2xl font-bold text-red-50">Member Report</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 mb-6">
          <CardHeader>
            <CardTitle className="text-red-50">Filter</CardTitle>
            <CardDescription className="text-gray-400">Filter data member yang sudah disubmit</CardDescription>
          </CardHeader>
          <CardContent>
            <MemberReportFilterBar
              marketingFilter={marketingFilter}
              setMarketingFilter={setMarketingFilter}
              websiteFilter={websiteFilter}
              setWebsiteFilter={setWebsiteFilter}
              followUpFilter={followUpFilter}
              setFollowUpFilter={setFollowUpFilter}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
              marketingList={marketingList}
              websiteList={websiteList}
              followUpList={followUpList}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              onPreset={handlePresetDate}
            />
          </CardContent>
        </Card>

        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-red-50">Data Member ({members.length})</CardTitle>
                <CardDescription className="text-gray-400">Member yang sudah disubmit oleh supervisor</CardDescription>
              </div>
              {canExport && (
                <Button
                  onClick={handleExportExcel}
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                  disabled={members.length === 0}
                >
                  <Download className="mr-2" size={16} />
                  Export Excel
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
              </div>
            ) : members.length === 0 ? (
              <div className="text-center py-12 text-gray-400">Tidak ada data member</div>
            ) : (
              <div className="rounded-lg border border-red-900/30 overflow-hidden">
                <MemberReportTable
                  user={user}
                  paginatedMembers={paginatedMembers}
                  onNote={openNoteDialog}
                  onEdit={handleEditMember}
                  onDelete={handleDeleteMember}
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <MemberEditDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleSaveEdit}
      />

      <MemberNoteDialog
        open={showNoteDialog}
        onOpenChange={setShowNoteDialog}
        noteMember={noteMember}
        noteText={noteText}
        setNoteText={setNoteText}
        memberNotes={memberNotes}
        onSubmit={handleSaveNote}
        saving={savingNote}
      />
    </div>
  );
};

export default MemberReportPanel;
